import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as api from './api'
import type { CreateDocumentTypeRequest, UpdateDocumentTypeRequest } from './types'

export const documentTypeKeys = {
  list: (configurationCode: string, activeOnly: boolean) =>
    ['products', configurationCode, 'document-types', activeOnly] as const,
}

export function useDocumentTypes(configurationCode: string | null | undefined, activeOnly = false) {
  return useQuery({
    queryKey: documentTypeKeys.list(configurationCode ?? '', activeOnly),
    queryFn: () => api.listDocumentTypes(configurationCode as string, activeOnly),
    enabled: !!configurationCode,
  })
}

/**
 * Łączy doc-types ze wszystkich produktów w jedną listę. Używane w FieldFormSheet
 * dla pól typu FILE - schemat jest globalny per object_type, ale doc_type jest
 * per-produkt (FK po id), więc admin musi widzieć wszystkie warianty.
 */
export function useAllDocumentTypes(configurationCodes: string[] | undefined, activeOnly = true) {
  return useQuery({
    queryKey: ['document-types-all', configurationCodes ?? [], activeOnly],
    queryFn: async () => {
      if (!configurationCodes || configurationCodes.length === 0) return []
      const results = await Promise.all(
        configurationCodes.map(pc => api.listDocumentTypes(pc, activeOnly)),
      )
      return results.flat()
    },
    enabled: !!configurationCodes && configurationCodes.length > 0,
  })
}

function invalidate(qc: ReturnType<typeof useQueryClient>, configurationCode: string) {
  qc.invalidateQueries({ queryKey: ['products', configurationCode, 'document-types'] })
}

export function useCreateDocumentType(configurationCode: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateDocumentTypeRequest) => api.createDocumentType(configurationCode, payload),
    onSuccess: () => invalidate(qc, configurationCode),
  })
}

export function useUpdateDocumentType(configurationCode: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: number; payload: UpdateDocumentTypeRequest }) =>
      api.updateDocumentType(configurationCode, vars.id, vars.payload),
    onSuccess: () => invalidate(qc, configurationCode),
  })
}

export function useDeleteDocumentType(configurationCode: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteDocumentType(configurationCode, id),
    onSuccess: () => invalidate(qc, configurationCode),
  })
}

export function useReorderDocumentTypes(configurationCode: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) => api.reorderDocumentTypes(configurationCode, ids),
    onSuccess: () => invalidate(qc, configurationCode),
  })
}
